import { useState, useEffect, useCallback, Fragment } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../api';

const QUELLE_LABELS = {
  manuell: 'Manuell',
  lv: 'Leistungsverzeichnis',
  angebot: 'Angebot',
  kalkulation: 'Kalkulation',
};
const TYP_LABELS = { material: 'Material', leistung: 'Leistung', misch: 'Mischposition' };

function fmtNum(n) {
  if (n == null || n === '') return '–';
  const v = parseFloat(n);
  if (isNaN(v)) return '–';
  return v.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 3 });
}
function fmtEur(n) {
  if (n == null || n === '') return '–';
  const v = parseFloat(n);
  if (isNaN(v)) return '–';
  return v.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

// Fix Datum-Shift (gleiches Muster wie toLocalDateStr in BautagebuchForm,
// bewusste Mini-Duplikation): pg liefert DATE-Spalten als ISO-Timestamp der
// LOKALEN Mitternacht in UTC ('…-14T22:00:00.000Z' fuer den 15.). Reines
// substring/slice(0,10) uebernimmt den UTC-Versatz (-1 Tag) - stattdessen
// als lokales Datum interpretieren und YYYY-MM-DD bauen.
function toLocalDateStr(v) {
  if (!v) return '';
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const dt = new Date(s);
  if (isNaN(dt.getTime())) return s.slice(0, 10);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}

// Hotfix Faktor-Anzeige: NUMERIC(10,3) kommt als '1.000' aus pg - im
// deutschen Kontext wie Tausend lesbar ('1 m -> 1.000 m'). Trailing-Nullen
// trimmen, max. 3 Nachkommastellen, NIE Tausenderpunkte: 1.000 -> '1',
// 0.500 -> '0.5', 0.125 -> '0.125'. Punkt als Dezimaltrenner, weil der
// Wert auch als value eines <input type=number> dient.
function fmtFaktor(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '';
  return String(parseFloat(n.toFixed(3)));
}

// Liefert Prozent-Fortschritt (0–999) für eine Position. null wenn keine
// Sollmenge gepflegt — dann zeigt die Tabelle "—".
function fortschrittProzent(p) {
  const soll = parseFloat(p.sollmenge);
  const ist = parseFloat(p.verbraucht_menge);
  if (!soll || isNaN(soll) || soll <= 0) return null;
  if (isNaN(ist) || ist <= 0) return 0;
  return Math.min((ist / soll) * 100, 999);
}

function ProgressBar({ prozent }) {
  if (prozent == null) return <span style={{ color: 'var(--text-3)' }}>–</span>;
  const breite = Math.min(prozent, 100);
  const farbe = prozent > 100 ? 'var(--red)' : 'var(--copper)';
  // Kein hartes minWidth: die Spalte muss in der breiten Projektpositionen-
  // Tabelle (11 Spalten) auch auf Tablet schrumpfen können, sonst sprengt sie
  // den Card-Inhalt nach rechts.
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 60 }}>
      <div style={{
        flex: '1 1 auto', height: 6, background: 'var(--bg-elevated)',
        borderRadius: 999, overflow: 'hidden', minWidth: 24,
      }}>
        <div style={{
          width: `${breite}%`, height: '100%', background: farbe,
          borderRadius: 999, transition: 'width 0.3s',
        }} />
      </div>
      <span className="text-mono" style={{
        fontSize: 12, fontWeight: 600,
        color: prozent > 100 ? 'var(--red)' : 'var(--text-2)',
        textAlign: 'right',
        whiteSpace: 'nowrap',
      }}>
        {prozent.toFixed(prozent >= 10 ? 0 : 1)} %
      </span>
    </div>
  );
}

export default function Projektpositionen() {
  const { user } = useAuth() || {};
  const navigate = useNavigate();
  const [baustellen, setBaustellen] = useState([]);
  const [baustelleId, setBaustelleId] = useState('');
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('aktiv');     // 'aktiv' | 'alle' | 'archiv'
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  // E3.x: aufgeklappte Langtexte (Positions-IDs) - Langtext erscheint NUR
  // per 📄-Toggle, nie dauerhaft in der Tabelle (User-Entscheidung 21.07.2026).
  const [langtextOffen, setLangtextOffen] = useState(() => new Set());
  const toggleLangtext = (id) => setLangtextOffen(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  // M1.2: Position, deren Material-Verknuepfungen (Rezept) gerade im Modal
  // bearbeitet werden - null = Modal zu.
  const [materialFor, setMaterialFor] = useState(null);

  useEffect(() => {
    if (user && user.role !== 'admin') { navigate('/'); return; }
    api.get('/baustellen').then(r => setBaustellen(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [user, navigate]);

  const load = useCallback(() => {
    if (!baustelleId) { setItems([]); return; }
    const archiviert = filter !== 'aktiv';
    api.get(`/projekt-positionen?baustelle_id=${baustelleId}${archiviert ? '&archiviert=1' : ''}`)
      .then(r => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]));
  }, [baustelleId, filter]);

  useEffect(() => { load(); }, [load]);

  const save = async (data) => {
    try {
      if (editing?.id) {
        await api.put(`/projekt-positionen/${editing.id}`, data);
      } else {
        await api.post('/projekt-positionen', { ...data, baustelle_id: baustelleId });
      }
      setShowForm(false);
      setEditing(null);
      load();
    } catch (e) {
      alert(e.response?.data?.error || 'Speichern fehlgeschlagen');
    }
  };

  const toggleAktiv = async (p) => {
    const aktion = p.aktiv ? 'archivieren' : 'reaktivieren';
    if (!window.confirm(`Position "${p.bezeichnung}" ${aktion}?`)) return;
    try {
      await api.put(`/projekt-positionen/${p.id}`, { aktiv: !p.aktiv });
      load();
    } catch (e) {
      alert(e.response?.data?.error || 'Aktion fehlgeschlagen');
    }
  };

  if (user?.role !== 'admin') return null;

  const filtered = items.filter(p => {
    if (filter === 'archiv' && p.aktiv) return false;
    if (filter === 'aktiv' && !p.aktiv) return false;
    if (search) {
      const q = search.toLowerCase();
      return (p.pos_nr || '').toLowerCase().includes(q)
          || (p.bezeichnung || '').toLowerCase().includes(q);
    }
    return true;
  });

  const aktivCount = items.filter(p => p.aktiv).length;

  const segBtn = (v, label) => (
    <button
      key={v}
      type="button"
      onClick={() => setFilter(v)}
      style={{
        background: filter === v ? 'var(--bg-card)' : 'transparent',
        border: 'none',
        padding: '6px 12px',
        borderRadius: 'var(--radius)',
        fontSize: 12,
        fontWeight: 600,
        color: filter === v ? 'var(--copper)' : 'var(--text-2)',
        cursor: 'pointer',
        fontFamily: 'inherit',
        boxShadow: filter === v ? 'var(--shadow)' : 'none',
      }}
    >{label}</button>
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Projektpositionen</div>
          <div className="page-sub">
            {baustelleId
              ? `${items.length} Positionen · ${aktivCount} aktiv`
              : 'Grundlage für LV, Angebote und Projektcontrolling'}
          </div>
        </div>
        <button
          className="btn btn-primary"
          disabled={!baustelleId}
          onClick={() => { setEditing({}); setShowForm(true); }}
          title={!baustelleId ? 'Erst Baustelle wählen' : 'Neue Position'}
        >+ Neue Position</button>
      </div>

      <div className="card" style={{ marginBottom: 14 }}>
        <div className="form-grid form-grid-2">
          <div className="form-group">
            <label className="form-label">Baustelle</label>
            <select
              className="form-select"
              value={baustelleId}
              onChange={e => setBaustelleId(e.target.value)}
            >
              <option value="">— Baustelle wählen —</option>
              {baustellen.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      {!baustelleId ? (
        <div className="card">
          <div className="empty">
            <div className="empty-icon">📋</div>
            <h3>Keine Baustelle gewählt</h3>
            <p>Oben eine Baustelle wählen, um deren Projektpositionen zu sehen oder anzulegen.</p>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-head" style={{ gap: 8, flexWrap: 'wrap' }}>
            <input
              className="form-input"
              placeholder="🔍 Pos.-Nr. oder Bezeichnung…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ maxWidth: 260, flex: '1 1 200px' }}
            />
            <div style={{ display: 'flex', gap: 4, background: 'var(--bg-elevated)', borderRadius: 'var(--radius)', padding: 4 }}>
              {segBtn('aktiv', 'Aktiv')}
              {segBtn('alle', 'Alle')}
              {segBtn('archiv', 'Archiviert')}
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="empty">
              <div className="empty-icon">📦</div>
              <h3>{items.length === 0 ? 'Noch keine Positionen' : 'Keine Treffer'}</h3>
              <p>{items.length === 0
                ? 'Lege die erste Position für diese Baustelle an.'
                : 'Filter oder Suche anpassen.'}
              </p>
              {items.length === 0 && (
                <button
                  className="btn btn-primary"
                  style={{ marginTop: 12 }}
                  onClick={() => { setEditing({}); setShowForm(true); }}
                >+ Erste Position</button>
              )}
            </div>
          ) : (
            <div className="table-wrap table-mobile-stack">
              <table>
                <thead>
                  <tr>
                    <th>Pos.-Nr.</th>
                    <th>Bezeichnung</th>
                    <th>Typ</th>
                    <th>Einheit</th>
                    <th>Sollmenge</th>
                    <th>Verbaut</th>
                    <th>Fortschritt</th>
                    <th>Einheitspreis</th>
                    <th>Quelle</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(p => {
                    const ist = parseFloat(p.verbraucht_menge) || 0;
                    const prozent = fortschrittProzent(p);
                    return (
                    <Fragment key={p.id}>
                    <tr style={{ opacity: p.aktiv ? 1 : 0.6 }}>
                      <td data-label="Pos.-Nr." className="text-mono text-copper" style={{ fontWeight: 600 }}>{p.pos_nr || '–'}</td>
                      <td data-label="" className="td-title" style={{ fontWeight: 500 }}>
                        {p.bezeichnung}
                        {p.nachtrag && <span className="badge badge-amber" style={{ marginLeft: 8 }}>Nachtrag</span>}
                        {p.langtext && (
                          <button
                            type="button"
                            onClick={() => toggleLangtext(p.id)}
                            title={langtextOffen.has(p.id) ? 'Langtext ausblenden' : 'Langtext anzeigen'}
                            style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-3)', cursor: 'pointer', fontSize: 11, padding: '2px 6px', marginLeft: 8 }}
                          >{langtextOffen.has(p.id) ? '▾ 📄' : '▸ 📄'}</button>
                        )}
                      </td>
                      <td data-label="Typ">{p.typ ? TYP_LABELS[p.typ] : '–'}</td>
                      <td data-label="Einheit" style={{ textAlign: 'center' }}>{p.einheit || '–'}</td>
                      <td data-label="Sollmenge" className="text-mono">{fmtNum(p.sollmenge)}</td>
                      <td data-label="Verbaut" className="text-mono" style={{ color: ist > 0 ? 'var(--text-1)' : 'var(--text-3)' }}>
                        {ist > 0 ? fmtNum(ist) : '0'}
                      </td>
                      <td data-label="Fortschritt"><ProgressBar prozent={prozent} /></td>
                      <td data-label="Einheitspreis" className="text-mono">{fmtEur(p.einheitspreis)}</td>
                      <td data-label="Quelle">
                        <span className="badge badge-blue">{QUELLE_LABELS[p.quelle_typ] || p.quelle_typ}</span>
                      </td>
                      <td data-label="Status">
                        {p.aktiv
                          ? <span className="badge badge-green">Aktiv</span>
                          : <span className="badge badge-grey">Archiviert</span>}
                      </td>
                      <td data-label="" className="td-actions">
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => setMaterialFor(p)}
                          title="Material-Verknüpfung (Rezept)"
                          style={p.material_links_count > 0 ? {} : { opacity: 0.55 }}
                        >🔗{p.material_links_count > 0 ? ` ${p.material_links_count}` : ''}</button>
                        <button className="btn btn-secondary btn-sm" onClick={() => { setEditing(p); setShowForm(true); }} title="Bearbeiten">✏️</button>
                        {p.aktiv ? (
                          <button className="btn btn-danger btn-sm" onClick={() => toggleAktiv(p)} title="Archivieren">🗑</button>
                        ) : (
                          <button className="btn btn-secondary btn-sm" onClick={() => toggleAktiv(p)} title="Reaktivieren">↩️</button>
                        )}
                      </td>
                    </tr>
                    {langtextOffen.has(p.id) && p.langtext && (
                      <tr>
                        <td></td>
                        <td colSpan={10} data-label="Langtext" style={{ paddingTop: 0 }}>
                          <div style={{
                            fontSize: 12, color: 'var(--text-2)', background: 'var(--bg-elevated)',
                            padding: '8px 10px', borderRadius: 'var(--radius)', whiteSpace: 'pre-wrap', lineHeight: 1.5,
                          }}>
                            {p.langtext}
                          </div>
                        </td>
                      </tr>
                    )}
                    </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {showForm && (
        <PositionForm
          position={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSave={save}
        />
      )}

      {materialFor && (
        <MaterialLinksModal
          position={materialFor}
          onClose={() => setMaterialFor(null)}
          onSaved={() => { setMaterialFor(null); load(); }}
        />
      )}
    </div>
  );
}

function PositionForm({ position, onClose, onSave }) {
  const isEdit = !!position?.id;
  const [data, setData] = useState({
    pos_nr: position?.pos_nr || '',
    bezeichnung: position?.bezeichnung || '',
    langtext: position?.langtext || '',
    typ: position?.typ || '',
    kategorie: position?.kategorie || '',
    einheit: position?.einheit || '',
    sollmenge: position?.sollmenge ?? '',
    einheitspreis: position?.einheitspreis ?? '',
    gesamtpreis: position?.gesamtpreis ?? '',
    quelle_typ: position?.quelle_typ || 'manuell',
    quelle_datei: position?.quelle_datei || '',
    nachtrag: !!position?.nachtrag,
    // Fix: substring(0,10) auf dem pg-ISO-Timestamp verschob das Datum um
    // -1 Tag (Anzeige UND beim Speichern real) - lokal interpretieren.
    nachtrag_datum: toLocalDateStr(position?.nachtrag_datum),
    nachtrag_grund: position?.nachtrag_grund || '',
  });
  const set = (k, v) => setData(d => ({ ...d, [k]: v }));

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 }} onClick={onClose}>
      <div className="card" style={{ maxWidth: 640, width: '100%', margin: 0, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div className="card-title" style={{ marginBottom: 14 }}>{isEdit ? 'Position bearbeiten' : 'Neue Position'}</div>

        <div className="form-section">Grunddaten</div>
        <div className="form-grid form-grid-2" style={{ marginBottom: 10 }}>
          <div className="form-group">
            <label className="form-label">Pos.-Nr.</label>
            <input className="form-input" value={data.pos_nr} onChange={e => set('pos_nr', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Quelle</label>
            <select className="form-select" value={data.quelle_typ} onChange={e => set('quelle_typ', e.target.value)}>
              <option value="manuell">Manuell</option>
              <option value="lv">Leistungsverzeichnis</option>
              <option value="angebot">Angebot</option>
              <option value="kalkulation">Kalkulation</option>
            </select>
          </div>
        </div>
        <div className="form-group" style={{ marginBottom: 10 }}>
          <label className="form-label">Bezeichnung *</label>
          <input className="form-input" value={data.bezeichnung} onChange={e => set('bezeichnung', e.target.value)} required />
        </div>
        <div className="form-group" style={{ marginBottom: 10 }}>
          <label className="form-label">Langtext (optional)</label>
          <textarea
            className="form-textarea"
            value={data.langtext}
            onChange={e => set('langtext', e.target.value)}
            placeholder="LV-Volltext / ausführliche Beschreibung — optional"
            rows={4}
          />
        </div>
        <div className="form-grid form-grid-3" style={{ marginBottom: 10 }}>
          <div className="form-group">
            <label className="form-label">Typ</label>
            <select className="form-select" value={data.typ} onChange={e => set('typ', e.target.value)}>
              <option value="">— offen —</option>
              <option value="material">Material</option>
              <option value="leistung">Leistung</option>
              <option value="misch">Mischposition</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Kategorie</label>
            <input className="form-input" value={data.kategorie} onChange={e => set('kategorie', e.target.value)} placeholder="optional" />
          </div>
          <div className="form-group">
            <label className="form-label">Einheit</label>
            <input className="form-input" value={data.einheit} onChange={e => set('einheit', e.target.value)} placeholder="z. B. m, Stück" />
          </div>
        </div>

        <div className="form-section">Mengen & Preise</div>
        <div className="form-grid form-grid-3" style={{ marginBottom: 10 }}>
          <div className="form-group">
            <label className="form-label">Sollmenge</label>
            <input className="form-input" type="number" step="0.001" value={data.sollmenge} onChange={e => set('sollmenge', e.target.value)} inputMode="decimal" />
          </div>
          <div className="form-group">
            <label className="form-label">Einheitspreis (€)</label>
            <input className="form-input" type="number" step="0.01" value={data.einheitspreis} onChange={e => set('einheitspreis', e.target.value)} inputMode="decimal" />
          </div>
          <div className="form-group">
            <label className="form-label">Gesamtpreis (€)</label>
            <input className="form-input" type="number" step="0.01" value={data.gesamtpreis} onChange={e => set('gesamtpreis', e.target.value)} inputMode="decimal" />
          </div>
        </div>
        <div className="form-group" style={{ marginBottom: 10 }}>
          <label className="form-label">Quelle-Datei (optional)</label>
          <input className="form-input" value={data.quelle_datei} onChange={e => set('quelle_datei', e.target.value)} placeholder="z. B. LV_Schule_2026.pdf" />
        </div>

        <div className="form-section">Nachtrag</div>
        <label className="check-group" style={{ background: 'var(--bg-elevated)', padding: '10px 14px', borderRadius: 'var(--radius)', marginBottom: 10 }}>
          <input type="checkbox" checked={data.nachtrag} onChange={e => set('nachtrag', e.target.checked)} />
          <span>Diese Position ist ein Nachtrag</span>
        </label>
        {data.nachtrag && (
          <>
            <div className="form-grid form-grid-2" style={{ marginBottom: 10 }}>
              <div className="form-group">
                <label className="form-label">Nachtrag-Datum</label>
                <input className="form-input" type="date" value={data.nachtrag_datum} onChange={e => set('nachtrag_datum', e.target.value)} />
              </div>
            </div>
            <div className="form-group" style={{ marginBottom: 10 }}>
              <label className="form-label">Nachtrag-Grund / Notiz</label>
              <textarea
                className="form-textarea"
                value={data.nachtrag_grund}
                onChange={e => set('nachtrag_grund', e.target.value)}
                placeholder="Worauf bezieht sich der Nachtrag? (z. B. Auftragserweiterung XY vom …)"
              />
            </div>
          </>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <button className="btn btn-secondary" onClick={onClose}>Abbrechen</button>
          <button className="btn btn-primary" onClick={() => onSave(data)}>{isEdit ? 'Speichern' : 'Anlegen'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── M1.2: Material-Verknuepfungs-Modal (Rezept/Stueckliste) ─────────────────
// Reine Stammdatenpflege: verknuepft die Position n:m mit Lager-Materialien
// samt Faktor (material_menge = positions_menge x faktor). Noch KEINE
// Automatik im Bautagebuch - die kommt mit M2.
// Archivierte Materialien: bestehende Links bleiben sichtbar (read-only
// Label + Badge) und duerfen gespeichert/entfernt werden; NEU waehlbar
// sind nur aktive Materialien (Dropdown = GET /api/material, nur aktive).
const NEU_MATERIAL_LEER = { pos_nr: '', bezeichnung: '', einheit: 'Stück', bestand: '', mindestbestand: '', notizen: '' };

function MaterialLinksModal({ position, onClose, onSaved }) {
  const [materialien, setMaterialien] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // M1.3: Inline-Neuanlage von Lager-Material. neuFor = Zeilen-Index, der
  // das Mini-Formular ausgeloest hat (dort wird das neue Material nach dem
  // Anlegen direkt ausgewaehlt). Kein zweites Modal - inline unter der Zeile.
  const [neuFor, setNeuFor] = useState(null);
  const [neuData, setNeuData] = useState(NEU_MATERIAL_LEER);
  const [neuSaving, setNeuSaving] = useState(false);
  const [neuError, setNeuError] = useState(null);

  useEffect(() => {
    let aktivFlag = true;
    Promise.all([
      api.get('/material'),
      api.get(`/projekt-positionen/${position.id}/material`),
    ]).then(([matR, linkR]) => {
      if (!aktivFlag) return;
      setMaterialien(Array.isArray(matR.data) ? matR.data : []);
      setRows((Array.isArray(linkR.data) ? linkR.data : []).map(l => ({
        material_id: l.material_id,
        // Hotfix: '1.000' aus pg wuerde im Input wie Tausend wirken -
        // beim Laden auf kurze Dezimalschreibweise normalisieren ('1', '0.5').
        faktor: fmtFaktor(l.faktor),
        bezeichnung: l.bezeichnung,
        einheit: l.einheit,
        material_aktiv: l.material_aktiv,
      })));
      setLoading(false);
    }).catch(() => {
      if (!aktivFlag) return;
      setError('Verknüpfungen konnten nicht geladen werden.');
      setLoading(false);
    });
    return () => { aktivFlag = false; };
  }, [position.id]);

  const setRow = (idx, patch) => setRows(rs => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const removeRow = (idx) => setRows(rs => rs.filter((_, i) => i !== idx));
  const addRow = () => setRows(rs => [...rs, { material_id: '', faktor: '1', material_aktiv: true }]);

  const matById = (mid) => materialien.find(m => m.id === Number(mid));

  // Client-Validierung (Server prueft identisch nochmal):
  // Material gewaehlt, Faktor >= 0.001, kein Material doppelt.
  const probleme = [];
  const seen = new Set();
  rows.forEach((r, i) => {
    const nr = i + 1;
    if (!r.material_id) probleme.push(`Zeile ${nr}: Material wählen`);
    else if (seen.has(Number(r.material_id))) probleme.push(`Zeile ${nr}: Material doppelt`);
    else seen.add(Number(r.material_id));
    const f = Number(r.faktor);
    if (r.faktor === '' || !Number.isFinite(f) || f < 0.001) {
      probleme.push(`Zeile ${nr}: Faktor mind. 0,001`);
    }
  });

  const speichern = async () => {
    if (probleme.length > 0 || saving) return;
    setSaving(true);
    setError(null);
    try {
      await api.put(`/projekt-positionen/${position.id}/material`, {
        material: rows.map(r => ({ material_id: Number(r.material_id), faktor: Number(r.faktor) })),
      });
      onSaved();
    } catch (e) {
      const d = e.response?.data;
      setError(d?.error ? (d.zeile != null ? `Zeile ${d.zeile}: ${d.error}` : d.error) : 'Speichern fehlgeschlagen');
      setSaving(false);
    }
  };

  // M1.3: Neues Lager-Material ueber den bestehenden POST /api/material
  // anlegen, Katalog neu laden, in der ausloesenden Zeile direkt auswaehlen.
  const neuAnlegenMoeglich = neuData.bezeichnung.trim() !== ''
    && (neuData.bestand === '' || Number.isFinite(Number(neuData.bestand)))
    && (neuData.mindestbestand === '' || Number.isFinite(Number(neuData.mindestbestand)))
    && !neuSaving;

  const materialAnlegen = async () => {
    if (!neuAnlegenMoeglich) return;
    setNeuSaving(true);
    setNeuError(null);
    try {
      const resp = await api.post('/material', {
        pos_nr: neuData.pos_nr.trim() || null,
        bezeichnung: neuData.bezeichnung.trim(),
        einheit: neuData.einheit.trim() || 'Stück',
        bestand: neuData.bestand === '' ? 0 : Number(neuData.bestand),
        mindestbestand: neuData.mindestbestand === '' ? 0 : Number(neuData.mindestbestand),
        notizen: neuData.notizen.trim() || null,
      });
      const matR = await api.get('/material');
      setMaterialien(Array.isArray(matR.data) ? matR.data : []);
      setRow(neuFor, { material_id: String(resp.data.id), material_aktiv: true });
      setNeuFor(null);
      setNeuData(NEU_MATERIAL_LEER);
    } catch (e) {
      setNeuError(e.response?.data?.error || 'Anlegen fehlgeschlagen');
    }
    setNeuSaving(false);
  };

  // Dubletten-Hinweis: aehnliche Bezeichnungen im Katalog anzeigen.
  const aehnlicheMat = neuFor != null && neuData.bezeichnung.trim().length >= 3
    ? materialien.filter(m => m.bezeichnung.toLowerCase().includes(neuData.bezeichnung.trim().toLowerCase())).slice(0, 3)
    : [];

  const posEinheit = position.einheit || 'Mengeneinheit';

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 }} onClick={onClose}>
      <div className="card" style={{ maxWidth: 680, width: '100%', margin: 0, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div className="card-title" style={{ marginBottom: 4 }}>
          🔗 Material-Verknüpfung
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 10 }}>
          <span className="text-mono text-copper" style={{ fontWeight: 600 }}>{position.pos_nr || '–'}</span>
          {' '}· {position.bezeichnung}
        </div>
        <div style={{
          background: 'var(--bg-elevated)', color: 'var(--text-3)', padding: '8px 12px',
          borderRadius: 'var(--radius)', fontSize: 12, marginBottom: 12, lineHeight: 1.5,
        }}>
          Basis: <strong>1 {posEinheit}</strong> dieser Position.
          {!position.einheit && ' (Position hat keine Einheit gepflegt — der Faktor bezieht sich auf 1 Mengeneinheit.)'}
          {' '}Der Faktor legt fest, wie viel Lager-Material je Positionseinheit verbraucht wird.
          Noch ohne Automatik — der automatische Lagerabzug im Bautagebuch folgt in einer späteren Phase.
        </div>

        {loading ? (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-3)' }}>
            <div className="spinner" style={{ margin: '0 auto 8px' }} />Lädt…
          </div>
        ) : (
          <>
            {rows.length === 0 && (
              <div style={{ padding: '14px 0', fontSize: 13, color: 'var(--text-3)', textAlign: 'center' }}>
                Noch keine Materialien verknüpft.
              </div>
            )}
            {rows.map((r, idx) => {
              const mat = r.material_aktiv === false ? null : matById(r.material_id);
              const matEinheit = r.material_aktiv === false ? r.einheit : (mat ? mat.einheit : null);
              return (
                <div key={idx}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8, flexWrap: 'wrap' }}>
                  <div style={{ flex: '2 1 240px' }}>
                    {r.material_aktiv === false ? (
                      <div className="form-input" style={{ display: 'flex', alignItems: 'center', gap: 8, opacity: 0.8 }}>
                        {r.bezeichnung}
                        <span className="badge badge-amber">archiviert</span>
                      </div>
                    ) : (
                      <select
                        className="form-select"
                        value={r.material_id}
                        onChange={e => {
                          if (e.target.value === '__neu__') {
                            // M1.3: Mini-Formular oeffnen; material_id bleibt
                            // leer, bis das neue Material angelegt ist - die
                            // Zeilen-Validierung blockiert Speichern solange.
                            setNeuFor(idx);
                            setNeuData(NEU_MATERIAL_LEER);
                            setNeuError(null);
                          } else {
                            setRow(idx, { material_id: e.target.value });
                          }
                        }}
                      >
                        <option value="">— Material wählen —</option>
                        <option value="__neu__">➕ Neues Lager-Material anlegen…</option>
                        {materialien.map(m => (
                          <option key={m.id} value={m.id}>
                            {m.bezeichnung} · {m.einheit} · Bestand {fmtNum(m.bestand)}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div style={{ flex: '1 1 110px' }}>
                    <input
                      className="form-input"
                      type="number"
                      step="0.001"
                      min="0.001"
                      value={r.faktor}
                      onChange={e => setRow(idx, { faktor: e.target.value })}
                      inputMode="decimal"
                      title="Faktor je 1 Positionseinheit"
                    />
                  </div>
                  <div style={{ flex: '2 1 170px', fontSize: 12, color: 'var(--text-3)', paddingTop: 10, lineHeight: 1.4 }}>
                    {/* Hotfix: nie den rohen Input-/DB-String zeigen -
                        fmtFaktor verhindert die '1.000'-als-Tausend-Optik. */}
                    1 {posEinheit} → {r.faktor === '' ? '?' : (fmtFaktor(r.faktor) || r.faktor)} {matEinheit || '…'}
                  </div>
                  <button className="btn btn-danger btn-sm" style={{ marginTop: 4 }} onClick={() => removeRow(idx)} title="Verknüpfung entfernen">✕</button>
                </div>

                {neuFor === idx && (
                  <div style={{
                    border: '1px solid var(--copper)', borderRadius: 'var(--radius)',
                    padding: 12, marginBottom: 10, background: 'var(--bg-elevated)',
                  }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1)', marginBottom: 2 }}>
                      ➕ Neues Lager-Material anlegen
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 10 }}>
                      Legt einen Eintrag im Material-Katalog (Lager) an — <strong>keine</strong> neue Projektposition.
                    </div>
                    <div className="form-group" style={{ marginBottom: 8 }}>
                      <label className="form-label">Bezeichnung *</label>
                      <input className="form-input" value={neuData.bezeichnung}
                        onChange={e => setNeuData(d => ({ ...d, bezeichnung: e.target.value }))} autoFocus />
                    </div>
                    {aehnlicheMat.length > 0 && (
                      <div style={{ fontSize: 11, color: 'var(--amber, #d49a3a)', marginBottom: 8 }}>
                        💡 Gibt es schon: {aehnlicheMat.map(m => `${m.bezeichnung} · ${m.einheit}`).join(' | ')}
                      </div>
                    )}
                    <div className="form-grid form-grid-2" style={{ marginBottom: 8 }}>
                      <div className="form-group">
                        <label className="form-label">Einheit</label>
                        <input className="form-input" value={neuData.einheit}
                          onChange={e => setNeuData(d => ({ ...d, einheit: e.target.value }))} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Pos.-Nr.</label>
                        <input className="form-input" value={neuData.pos_nr}
                          onChange={e => setNeuData(d => ({ ...d, pos_nr: e.target.value }))} placeholder="optional" />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Bestand</label>
                        <input className="form-input" type="number" step="0.01" value={neuData.bestand}
                          onChange={e => setNeuData(d => ({ ...d, bestand: e.target.value }))} placeholder="0" inputMode="decimal" />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Mindestbestand</label>
                        <input className="form-input" type="number" step="0.01" value={neuData.mindestbestand}
                          onChange={e => setNeuData(d => ({ ...d, mindestbestand: e.target.value }))} placeholder="0" inputMode="decimal" />
                      </div>
                    </div>
                    <div className="form-group" style={{ marginBottom: 10 }}>
                      <label className="form-label">Notizen</label>
                      <textarea className="form-textarea" value={neuData.notizen}
                        onChange={e => setNeuData(d => ({ ...d, notizen: e.target.value }))}
                        placeholder="optional" rows={2} />
                    </div>
                    {neuError && (
                      <div style={{
                        marginBottom: 8, padding: '8px 10px', borderRadius: 'var(--radius)',
                        background: 'rgba(220,80,80,0.12)', border: '1px solid rgba(220,80,80,0.4)',
                        color: 'var(--text-1)', fontSize: 12,
                      }}>⚠️ {neuError}</div>
                    )}
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <button className="btn btn-secondary btn-sm" disabled={neuSaving}
                        onClick={() => { setNeuFor(null); setNeuError(null); }}>Abbrechen</button>
                      <button className="btn btn-primary btn-sm" disabled={!neuAnlegenMoeglich}
                        style={{ opacity: neuAnlegenMoeglich ? 1 : 0.5 }}
                        onClick={materialAnlegen}>
                        {neuSaving ? '⏳ Legt an…' : 'Anlegen'}
                      </button>
                    </div>
                  </div>
                )}
                </div>
              );
            })}

            <button className="btn btn-secondary btn-sm" onClick={addRow} style={{ marginTop: 4 }}>
              + Material hinzufügen
            </button>

            {probleme.length > 0 && rows.length > 0 && (
              <div style={{ marginTop: 10, fontSize: 12, color: 'var(--amber, #d49a3a)', lineHeight: 1.5 }}>
                {probleme.map((p, i) => <div key={i}>⚠ {p}</div>)}
              </div>
            )}
            {error && (
              <div style={{
                marginTop: 10, padding: '10px 12px', borderRadius: 'var(--radius)',
                background: 'rgba(220,80,80,0.12)', border: '1px solid rgba(220,80,80,0.4)',
                color: 'var(--text-1)', fontSize: 13,
              }}>⚠️ {error}</div>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
              <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Abbrechen</button>
              <button
                className="btn btn-primary"
                onClick={speichern}
                disabled={probleme.length > 0 || saving}
                style={{ opacity: probleme.length > 0 || saving ? 0.5 : 1 }}
              >{saving ? '⏳ Speichert…' : 'Speichern'}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

